/**
 * GuestPortalHome — `/guest/:token`, lo primero que ve el huésped.
 *
 * Responde a tres preguntas y a ninguna más:
 *   ¿cuándo y dónde es mi estancia?   ¿qué tengo que hacer ahora?   ¿cómo os aviso?
 *
 * Una sola acción siguiente, siempre. No una lista de pendientes: el huésped
 * no está operando un hotel, está yéndose de viaje.
 *
 * Lo que NO aparece aquí, a propósito (§7.3.2, §7.4):
 *   - el documento de nadie, ni siquiera el suyo: el portal lo recoge pero no
 *     lo devuelve;
 *   - los datos personales de los acompañantes: el portal es de escritura para
 *     los datos de los demás. Del grupo solo se dice cuántos son;
 *   - el reporte de extranjeros: es trabajo de la propiedad y el huésped no
 *     tiene ninguna acción ahí;
 *   - nada operativo interno: ni dinero, ni canal, ni puntuación de
 *     asignación, ni notas del equipo.
 */
import React, { useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import {
  CaretRight, Check, MapPin, Clock, AirplaneTilt, Boat, Car, Phone,
  WhatsappLogo, EnvelopeSimple,
} from "@phosphor-icons/react";

import { useSettingsV2 } from "@/lib/settingsV2Store";
import { usePmsReservations } from "@/lib/pmsRuntimeStore";
import { useGuestsV2 } from "@/lib/guestsV2Store";
import {
  portalViewForToken, stayDatesLabel, partyLabel, dayLabel,
} from "@/lib/guestPortalV2Selectors";
import { CONTACT_BOUNDARY_NOTE } from "@/data/guestPortalV2Demo";
import GuestShell, { GuestLinkInvalid, TAP } from "@/pages/guest/GuestShell";

const CARD = "rounded-2xl bg-white border border-stone-200/80 p-4";
const MODE_ICON = { plane: AirplaneTilt, boat: Boat, other: Car };

export default function GuestPortalHome() {
  const { token } = useParams();
  const settings = useSettingsV2();
  const reservations = usePmsReservations();
  const guestState = useGuestsV2();

  const view = useMemo(
    () => portalViewForToken({ token, reservations, settings, guestState }),
    [token, reservations, settings, guestState],
  );

  if (!view) return <GuestLinkInvalid />;

  const { stay, next_action: next, arrival, property } = view;

  return (
    <GuestShell
      token={token}
      area="home"
      title={view.phase_label}
      subtitle={property.name}
    >
      {/* Lo primero: cuándo. Es la pregunta que el huésped se hace al abrir. */}
      <section className={`${CARD} mb-3`} data-testid="guest-home-stay">
        <p className="font-serif-dv text-xl text-stone-900" data-testid="guest-home-dates">
          {stayDatesLabel(stay)}
        </p>
        <p className="text-sm text-stone-500 mt-1" data-testid="guest-home-party">
          {view.guest_name} · {partyLabel(view.party_size)}
        </p>

        <dl className="mt-4 pt-4 border-t border-stone-100 space-y-2.5 text-sm">
          {/* El tipo contratado, y nada más. La villa concreta no se nombra:
              la asignación puede cambiar y no hay regla aprobada que diga
              cuándo deja de poder hacerlo (ver `guestPortalV2Selectors`). */}
          {view.unit_type && (
            <Row label="Alojamiento" testId="guest-home-unit-type">
              {view.unit_type}
            </Row>
          )}
          <Row label="Entrada" testId="guest-home-checkin-time">
            {dayLabel(stay.check_in)} · a partir de las {stay.check_in_time}
          </Row>
          <Row label="Salida" testId="guest-home-checkout-time">
            {dayLabel(stay.check_out)} · hasta las {stay.check_out_time}
          </Row>
        </dl>
      </section>

      {/* Lo segundo: qué hacer. Una cosa. */}
      <section
        className={`${CARD} mb-3 ${next.tone === "amber" ? "border-amber-300 bg-amber-50/60" : ""}`}
        data-testid="guest-home-next"
      >
        <div className="flex items-start gap-3">
          {next.tone === "emerald" && (
            <span className="w-7 h-7 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
              <Check size={15} weight="bold" />
            </span>
          )}
          <div className="min-w-0">
            <p className="text-[15px] text-stone-900" data-testid="guest-home-next-title">
              {next.title}
            </p>
            {next.detail && (
              <p className="text-sm text-stone-500 mt-0.5">{next.detail}</p>
            )}
          </div>
        </div>
        {next.cta && (
          <Link
            to={`/guest/${token}/${next.cta.area}`}
            data-testid="guest-home-next-cta"
            className={`${TAP} mt-3 w-full rounded-full bg-stone-900 text-white text-sm flex items-center justify-center gap-1.5`}
          >
            {next.cta.label} <CaretRight size={13} weight="bold" />
          </Link>
        )}
      </section>

      {/* Lo tercero: cómo llega. Solo si ya nos lo ha contado. */}
      {arrival && (
        <section className={`${CARD} mb-3`} data-testid="guest-home-arrival">
          <h2 className="text-[10px] tracking-[0.25em] uppercase text-stone-400 mb-3">
            Tu llegada
          </h2>
          <ul className="space-y-2.5 text-sm">
            <IconRow icon={MODE_ICON[arrival.mode] || Car} testId="guest-arrival-mode-line">
              {arrival.mode_label}
              {arrival.reference && (
                <span className="text-stone-400"> · {arrival.reference}</span>
              )}
            </IconRow>
            {arrival.landing_time && (
              <IconRow icon={Clock} testId="guest-arrival-landing-line">
                {arrival.landing_label} {arrival.landing_time}
              </IconRow>
            )}
            {arrival.estimated_arrival && (
              <IconRow icon={MapPin} testId="guest-arrival-eta-line">
                Te esperamos sobre las {arrival.estimated_arrival}
              </IconRow>
            )}
            {/* Nada de aeropuertos ni puertos para quien llega por su cuenta:
                no se le preguntó y no hay recogida que confirmarle. */}
            {arrival.transfer && (
              <IconRow icon={Car} testId="guest-arrival-transfer-line">
                {arrival.transfer}
              </IconRow>
            )}
          </ul>
        </section>
      )}

      <ContactCard property={property} contact={view.contact} />
    </GuestShell>
  );
}

function Row({ label, children, testId }) {
  return (
    <div className="flex items-baseline justify-between gap-3" data-testid={testId}>
      <dt className="text-stone-400 shrink-0">{label}</dt>
      <dd className="text-stone-900 text-right">{children}</dd>
    </div>
  );
}

function IconRow({ icon: Icon, children, testId }) {
  return (
    <li className="flex items-start gap-2.5 text-stone-700" data-testid={testId}>
      <Icon size={15} className="mt-0.5 shrink-0 text-stone-400" />
      <span>{children}</span>
    </li>
  );
}

/**
 * Contacto de la propiedad, tal como está configurado en Ajustes.
 *
 * No hay mensajería de huésped todavía y no se finge que la haya: estos son
 * enlaces del teléfono del huésped (llamar, WhatsApp, correo), no un hilo
 * dentro del portal. El WhatsApp de los leads de venta (§16.7) NO se reutiliza
 * aquí: es otra conversación, con otro consentimiento.
 */
function ContactCard({ property, contact }) {
  const links = [
    property.contact_whatsapp && {
      key: "whatsapp", icon: WhatsappLogo, label: "WhatsApp",
      href: `https://wa.me/${String(property.contact_whatsapp).replace(/[^\d]/g, "")}`,
    },
    property.contact_phone && {
      key: "phone", icon: Phone, label: "Llamar",
      href: `tel:${String(property.contact_phone).replace(/\s/g, "")}`,
    },
    property.contact_email && {
      key: "email", icon: EnvelopeSimple, label: "Email",
      href: `mailto:${property.contact_email}`,
    },
  ].filter(Boolean);

  return (
    <section className={CARD} data-testid="guest-home-contact">
      <h2 className="text-[10px] tracking-[0.25em] uppercase text-stone-400 mb-3">
        ¿Necesitas algo?
      </h2>
      <ul className="flex flex-wrap gap-2">
        {links.map((l) => (
          <li key={l.key}>
            <a
              href={l.href}
              data-testid={`guest-contact-${l.key}`}
              className={`${TAP} inline-flex items-center gap-1.5 px-4 rounded-full border border-stone-300 text-sm text-stone-700`}
            >
              <l.icon size={15} /> {l.label}
            </a>
          </li>
        ))}
      </ul>
      <p className="text-[11px] text-stone-400 mt-3" data-testid="guest-contact-note">
        {CONTACT_BOUNDARY_NOTE}
      </p>
      {/* Lo que consta de él, para que pueda corregirlo si no es suyo. Lo que
          no consta se dice que no consta; no se rellena con nada. */}
      <p className="text-[11px] text-stone-400 mt-2" data-testid="guest-contact-own">
        Te avisaremos en {contact.email || "— sin email —"}
        {contact.phone ? ` · ${contact.phone}` : " · sin teléfono"}.
      </p>
    </section>
  );
}
